import * as React from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '~/lib/utils'

// 미니멀 체크박스 — 숨긴 native input(peer) + 커스텀 박스. 시맨틱 토큰만, 다크 자동 적응.
// indeterminate prop은 DOM 프로퍼티라 ref/effect로 동기화한다. id 없으면 useId로 연결.
export type CheckboxProps = Omit<React.ComponentProps<'input'>, 'type'> & {
    label?: React.ReactNode
    /** 중간 상태(일부 선택). React엔 prop이 없어 ref로 el.indeterminate를 설정한다. */
    indeterminate?: boolean
}

/** 체크박스. label(children/label prop)을 input과 연결한다. */
function Checkbox({
    className,
    label,
    children,
    disabled,
    indeterminate,
    id,
    ref,
    ...props
}: CheckboxProps) {
    const reactId = React.useId()
    const inputId = id ?? reactId
    const text = label ?? children
    const innerRef = React.useRef<HTMLInputElement>(null)
    // indeterminate는 DOM 프로퍼티라 effect로 동기화. 외부 ref도 함께 연결한다.
    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)
    React.useEffect(() => {
        if (innerRef.current) innerRef.current.indeterminate = !!indeterminate
    }, [indeterminate, props.checked])

    return (
        <label
            htmlFor={inputId}
            className={cn(
                'text-foreground inline-flex items-center gap-2 text-[13px] select-none',
                disabled && 'opacity-50',
                !disabled && 'cursor-pointer',
                className
            )}>
            <input
                ref={innerRef}
                id={inputId}
                type='checkbox'
                disabled={disabled}
                data-slot='checkbox'
                aria-checked={indeterminate ? 'mixed' : undefined}
                className='peer sr-only'
                {...props}
            />
            <span
                aria-hidden
                className='border-input bg-card text-primary-foreground peer-checked:border-primary peer-checked:bg-primary peer-indeterminate:border-primary peer-indeterminate:bg-primary peer-focus-visible:ring-ring/40 flex size-4 shrink-0 items-center justify-center rounded-[var(--radius-sm)] border transition-colors peer-focus-visible:ring-2 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 peer-indeterminate:[&>svg]:opacity-100'>
                {indeterminate ? (
                    <Minus className='size-3' strokeWidth={3} />
                ) : (
                    <Check className='size-3' strokeWidth={3} />
                )}
            </span>
            {text != null && <span>{text}</span>}
        </label>
    )
}

export { Checkbox }
